import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  BadgeCheck,
  CalendarClock,
  CreditCard,
  FileText,
  History,
  Receipt,
  RefreshCw,
  TriangleAlert,
} from 'lucide-react';
import { PaymentStatus, SubscriptionStatus } from '../types';
import { usePaymentStore } from '../state/usePaymentStore';
import { usePaymentGate } from '../hooks/usePaymentGate';
import { WebPurchaseNotice } from '../components/WebPurchaseNotice';

/**
 * ─── Écran : Mon abonnement ──────────────────────────────────────────────────
 * Statut + renouvellement + moyen de paiement + historique + factures,
 * organisés en onglets. Couvre aussi l'état "Abonnement expiré".
 */

type Tab = 'subscription' | 'history' | 'invoices';

const STATUS_LABEL: Partial<Record<PaymentStatus, { text: string; cls: string }>> = {
  [PaymentStatus.SUCCESS]: { text: 'Réussi', cls: 'bg-green-500/15 text-green-400' },
  [PaymentStatus.FAILED]: { text: 'Échoué', cls: 'bg-red-500/15 text-red-400' },
  [PaymentStatus.CANCELLED]: { text: 'Annulé', cls: 'bg-overlay-2 text-fg-faint' },
  [PaymentStatus.EXPIRED]: { text: 'Expiré', cls: 'bg-amber-500/15 text-amber-400' },
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function SubscriptionPage() {
  const navigate = useNavigate();
  const { paymentsEnabled, canPurchaseInApp, daysRemaining } = usePaymentGate();
  const subscription = usePaymentStore((s) => s.subscription);
  const plans = usePaymentStore((s) => s.plans);
  const transactions = usePaymentStore((s) => s.transactions);
  const invoices = usePaymentStore((s) => s.invoices);
  const [tab, setTab] = useState<Tab>('subscription');

  if (!paymentsEnabled) return <Navigate to="/dashboard" replace />;

  const plan = plans.find((p) => p.id === subscription?.planId);
  const isActive = subscription?.status === SubscriptionStatus.ACTIVE;
  const isExpired = subscription?.status === SubscriptionStatus.EXPIRED;

  const tabs: { id: Tab; label: string; icon: JSX.Element }[] = [
    { id: 'subscription', label: 'Abonnement', icon: <BadgeCheck className="w-4 h-4" /> },
    { id: 'history', label: 'Historique', icon: <History className="w-4 h-4" /> },
    { id: 'invoices', label: 'Factures', icon: <Receipt className="w-4 h-4" /> },
  ];

  return (
    <div className="min-h-screen bg-app px-4 py-8">
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => navigate('/dashboard')}
          className="inline-flex items-center gap-1.5 text-xs text-fg-faint hover:text-fg-subtle transition-colors mb-6"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Retour
        </button>

        <h1 className="text-2xl font-bold text-fg mb-6">Mon abonnement</h1>

        {/* Onglets */}
        <div className="flex gap-2 mb-6">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold transition-colors ${
                tab === t.id
                  ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white'
                  : 'bg-overlay-1 border border-line text-fg-muted hover:bg-overlay-2'
              }`}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* ── Onglet Abonnement ── */}
        {tab === 'subscription' && (
          <div className="space-y-4">
            {!subscription && (
              <div className="bg-overlay-1 border border-line rounded-2xl p-8 text-center">
                <p className="text-sm text-fg-subtle mb-5">
                  Vous n'avez pas encore d'abonnement.
                </p>
                {canPurchaseInApp ? (
                  <Link
                    to="/plans"
                    className="inline-flex px-5 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl text-sm font-semibold"
                  >
                    Découvrir les plans
                  </Link>
                ) : (
                  <WebPurchaseNotice className="text-xs text-fg-subtle leading-relaxed" />
                )}
              </div>
            )}

            {isExpired && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-6">
                <div className="flex items-start gap-3">
                  <TriangleAlert className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h2 className="text-sm font-bold text-fg mb-1">Abonnement expiré</h2>
                    <p className="text-xs text-fg-subtle mb-4">
                      Votre abonnement {plan?.name ?? ''} a expiré le{' '}
                      {subscription && formatDate(subscription.expiresAt)}. Renouvelez-le
                      pour retrouver l'accès complet.
                    </p>
                    {canPurchaseInApp ? (
                      <button
                        onClick={() => navigate(plan ? `/checkout/${plan.id}` : '/plans')}
                        className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl text-xs font-semibold"
                      >
                        <RefreshCw className="w-3.5 h-3.5" /> Renouveler maintenant
                      </button>
                    ) : (
                      <WebPurchaseNotice className="text-xs text-fg-subtle leading-relaxed" />
                    )}
                  </div>
                </div>
              </div>
            )}

            {isActive && subscription && plan && (
              <div className="bg-overlay-1 border border-line rounded-2xl p-6 space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-bold text-fg">Plan {plan.name}</p>
                    <p className="text-xs text-fg-faint">
                      {formatPrice(plan.price, plan.currency)} / mois
                    </p>
                  </div>
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-500/15 text-green-400 text-[11px] font-bold rounded-full">
                    <BadgeCheck className="w-3.5 h-3.5" /> Actif
                  </span>
                </div>

                <div className="flex items-center gap-3 bg-overlay-2 border border-line rounded-xl px-4 py-3">
                  <CalendarClock className="w-5 h-5 text-fg-faint" />
                  <div className="flex-1 text-xs">
                    <p className="text-fg-muted">
                      Expire le <span className="font-semibold text-fg">{formatDate(subscription.expiresAt)}</span>
                    </p>
                    {daysRemaining !== null && (
                      <p className="text-fg-faint">{daysRemaining} jour(s) restant(s)</p>
                    )}
                  </div>
                  {canPurchaseInApp && (
                    <button
                      onClick={() => navigate(`/checkout/${plan.id}`)}
                      className="text-xs font-semibold text-green-400 hover:text-green-300"
                    >
                      Renouveler
                    </button>
                  )}
                </div>

                {/* Moyen de paiement */}
                <div className="flex items-center gap-3 bg-overlay-2 border border-line rounded-xl px-4 py-3">
                  <CreditCard className="w-5 h-5 text-fg-faint" />
                  <div className="flex-1 text-xs">
                    <p className="text-fg-muted">Moyen de paiement</p>
                    <p className="font-semibold text-fg">
                      {subscription.paymentMethodLabel ?? '—'}
                    </p>
                  </div>
                  {canPurchaseInApp && (
                    <button
                      onClick={() => navigate(`/checkout/${plan.id}`)}
                      className="text-xs font-semibold text-green-400 hover:text-green-300"
                    >
                      Changer
                    </button>
                  )}
                </div>

                <p className="text-[11px] text-fg-faint leading-relaxed">
                  Le renouvellement prolonge votre abonnement à partir de sa date
                  d'expiration. Le moyen de paiement est confirmé à chaque
                  transaction — aucun prélèvement automatique.
                </p>
              </div>
            )}
          </div>
        )}

        {/* ── Onglet Historique ── */}
        {tab === 'history' && (
          <div className="bg-overlay-1 border border-line rounded-2xl divide-y divide-line">
            {transactions.length === 0 && (
              <p className="p-8 text-center text-sm text-fg-subtle">
                Aucun paiement pour le moment.
              </p>
            )}
            {transactions.map((tx) => {
              const label = STATUS_LABEL[tx.status] ?? {
                text: tx.status,
                cls: 'bg-overlay-2 text-fg-faint',
              };
              return (
                <div key={tx.reference} className="flex items-center gap-3 px-5 py-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-fg truncate">
                      {plans.find((p) => p.id === tx.planId)?.name ?? tx.planId}
                    </p>
                    <p className="text-[11px] text-fg-faint font-mono truncate">
                      {tx.reference} · {formatDate(tx.createdAt)}
                    </p>
                    {tx.failureReason && (
                      <p className="text-[11px] text-red-400/80 truncate">{tx.failureReason}</p>
                    )}
                  </div>
                  <p className="text-sm font-bold text-fg">
                    {formatPrice(tx.amount, tx.currency)}
                  </p>
                  <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${label.cls}`}>
                    {label.text}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Onglet Factures ── */}
        {tab === 'invoices' && (
          <div className="bg-overlay-1 border border-line rounded-2xl divide-y divide-line">
            {invoices.length === 0 && (
              <p className="p-8 text-center text-sm text-fg-subtle">
                Aucune facture disponible.
              </p>
            )}
            {invoices.map((inv) => (
              <div key={inv.id} className="flex items-center gap-3 px-5 py-4">
                <FileText className="w-5 h-5 text-fg-faint flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-fg">{inv.id}</p>
                  <p className="text-[11px] text-fg-faint">
                    {inv.planName} · {formatDate(inv.periodStart)} → {formatDate(inv.periodEnd)}
                  </p>
                </div>
                <p className="text-sm font-bold text-fg">
                  {formatPrice(inv.amount, inv.currency)}
                </p>
                <button
                  onClick={() => window.print()}
                  className="text-xs font-semibold text-green-400 hover:text-green-300"
                >
                  Imprimer
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
